import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'ramda';
import { makeStyles } from '@material-ui/core';
import { selectIsManagerMode, selectUserId } from 'src/store/selectors/userInfo';
import WithAuthCheck from 'src/hoc/AuthCheck';
import { homeRoute, routes, selfService } from 'src/routes/routes';
import useRoutes from 'src/routes/useRoutes';
import { useAuth } from 'react-oidc-context';
import ConfigLoader from './ConfigLoader';
import { readSidebarOpen, writeSidebarOpen } from './sidebarState';
import Header from './Header';
import NavBar from './NavBar';
const drawerWidth = 240;
const useStyles = makeStyles((theme) => ({
    container: {
        minWidth: '100%',
        minHeight: '100%',
        display: 'flex'
    },
    content: {
        width: `calc(100% - ${drawerWidth}px)`,
        marginTop: 64,
        height: 'calc(100% - 64px)',
        flexGrow: 1,
        backgroundColor: theme.palette.background.default,
        overflow: 'auto',
        '@media print': {
            marginTop: theme.spacing(1),
            height: 'initial',
            padding: 0
        }
    },
    content2: {
        width: '100%',
        height: '100%',
        flexGrow: 1,
        backgroundColor: theme.palette.background.default,
        overflow: 'auto',
        position: 'relative'
    }
}));
function GeneralLayout({ children }) {
    const { user, isLoading } = useAuth();
    const classes = useStyles();
    const { getAllowedRoutes, selectedRoute } = useRoutes();
    const noTranslationsLoaded = useSelector((state) => isEmpty(state.translation));
    const isManagerMode = useSelector(selectIsManagerMode);
    const userId = useSelector(selectUserId);
    const initSuccess = useSelector((state) => (state.configurations.initialized && state.userInfo.loaded && state.userInfo.versionsLoaded));
    const isLoggedIn = user !== null || isLoading || noTranslationsLoaded;
    const [drawerOpen, setDrawerOpen] = useState(true);
    // Restore the persisted sidebar state once the user id is known (it may not be
    // available yet on the very first render, before user info has loaded), so we always
    // read the correct per-user key rather than the anonymous fallback.
    useEffect(() => {
        if (userId != null) {
            setDrawerOpen(readSidebarOpen(userId, true));
        }
    }, [userId]);
    const routesForNavigation = useMemo(() => (getAllowedRoutes(isManagerMode ? [homeRoute, ...routes] : selfService)), [getAllowedRoutes, isManagerMode]);
    const toggleDrawer = useCallback((flag = !drawerOpen) => {
        setDrawerOpen(flag);
        // Persist only under a known per-user key (mirrors the guarded restore above).
        if (userId != null) {
            writeSidebarOpen(userId, flag);
        }
    }, [drawerOpen, userId]);
    return isLoggedIn && !initSuccess ? (React.createElement(ConfigLoader, null)) : (
    // @ts-ignore
    React.createElement(WithAuthCheck, { isManagerMode: isManagerMode },
        React.createElement("div", { id: "app-container", className: classes.container },
            React.createElement(Header, { drawerOpen: drawerOpen, selectedNav: selectedRoute }),
            React.createElement(NavBar, { drawerOpen: drawerOpen, toggleDrawer: toggleDrawer, selectedNav: selectedRoute, routesForNavigation: routesForNavigation, isManagerMode: isManagerMode }),
            React.createElement("main", { className: user?.profile?.name ? classes.content : classes.content2 }, children))));
}
export default GeneralLayout;
